/**
 * SectionVisibilityPanel — Admin overlay for setting each report section's
 * access level. Three states per section:
 *   Hidden — not rendered, no nav entry.
 *   Public — visible to anyone with the URL.
 *   Authed — visible only after the viewer signs in with the report PIN.
 *
 * Persists to `kuutio_instances.settings.reportSectionAccess` (new). The
 * legacy `settings.reportSections` boolean field is kept untouched so old
 * code paths still work; the resolver in `sectionAccess.js` prefers the
 * new field when present and falls back to the legacy boolean otherwise.
 */
import React, { useState, useEffect, useCallback } from 'react';
import styled from '@emotion/styled';
import { Eye, EyeOff, Loader2, AlertTriangle, Lock } from 'lucide-react';
import { useReportEdits } from './ReportEditsContext';
import { API_ENDPOINTS } from '../../config/api';
import {
  ACCESS_HIDDEN, ACCESS_PUBLIC, ACCESS_AUTHED, resolveSectionAccess,
} from './sectionAccess';

// Mirrors the section list rendered by PublicWorkshopReport. defaultVisible
// matches the showSection() defaults there. todoIn = future work tag shown
// as a chip — these sections exist in the visibility list (so admin can
// pre-toggle them) before V3.* lands them in the rendered report.
const SECTIONS = [
  { id: 'stats',         label: 'Tiivistelmä',                    defaultVisible: true  },
  { id: 'future-story',  label: 'Toivottu tulevaisuus',           defaultVisible: true  },
  { id: 'phase-0',       label: 'Skenaariot ja tulevaisuuskuvat', defaultVisible: true  },
  { id: 'phase-1',       label: 'Nykytila ja rajaus',             defaultVisible: true  },
  { id: 'foresight-brief', label: 'Ennakointi: rajaus',           defaultVisible: true  },
  { id: 'change-drivers', label: 'Muutosvoimat',                  defaultVisible: true  },
  { id: 'foresight-scenarios', label: 'Muodostetut skenaariot',   defaultVisible: true  },
  { id: 'preferred-future', label: 'Tavoiteltava tulevaisuus',    defaultVisible: true  },
  { id: 'backcasting-path', label: 'Toimintapolku',               defaultVisible: true  },
  { id: 'stress-test',      label: 'Villi kortti -stressitesti',  defaultVisible: true  },
  { id: 'scenarios',     label: 'Vaikutuskartta',                 defaultVisible: true  },
  { id: 'pivotpoints',   label: 'Vipupisteet',                    defaultVisible: true  },
  { id: 'lenses',        label: 'Esteet ja mahdollisuudet',       defaultVisible: true  },
  { id: 'pain-points',   label: 'Kipupisteiden keruu',            defaultVisible: true  },
  { id: 'kipupisteet',   label: 'Ongelmat',                       defaultVisible: true  },
  { id: 'ankkurit',      label: 'Muutoksen esteet',               defaultVisible: true  },
  { id: 'juurisyyt',     label: 'Juurisyyt',                      defaultVisible: true  },
  { id: 'leverage-points', label: 'Vipupisteet ja mittarit',      defaultVisible: true  },
  { id: 'vipupisteet-v2', label: 'Vipupisteet (Ongelmakartta v3)', defaultVisible: true  },
  { id: 'solutions-v4', label: 'Ratkaisut vipupisteittäin (Ratkaisut v4)', defaultVisible: true  },
  { id: 'future-interplay-v2', label: 'Ratkaisujen ristivaikutukset (Tulevaisuustestaus v2)', defaultVisible: true },
  { id: 'future-timeline-v2', label: 'Tulevaisuustestaus (Tulevaisuustestaus v2)', defaultVisible: true },
  { id: 'systemic-analysis', label: 'Ongelmakartan analyysi',     defaultVisible: true  },
  { id: 'mitajos',       label: 'Mitä jos?',                      defaultVisible: true  },
  { id: 'solutions',     label: 'Ratkaisut',                      defaultVisible: true  },
  { id: 'solution-projections', label: 'Ratkaisujen vaikutus mittareihin', defaultVisible: true },
  { id: 'future-interplay', label: 'Ratkaisujen ristivaikutukset', defaultVisible: true },
  { id: 'future-timeline', label: 'Tulevaisuustestaus', defaultVisible: true },
  { id: 'foresight-assumptions', label: 'Oletukset ja tarkistuspisteet', defaultVisible: true },
  { id: 'participants',  label: 'Osallistujat',                   defaultVisible: true  },
  { id: 'sources',       label: 'Tietopohja',                     defaultVisible: true  },
  { id: 'org-actions',         label: 'Toimenpiteet',                       defaultVisible: false },
  { id: 'first-steps',         label: 'Mitä tehdään ensiksi?',              defaultVisible: false },
  { id: 'implementation-plans', label: 'Miten aloitamme? (per ratkaisu)',    defaultVisible: true  },
];

const Container = styled.div`
  height: 100%;
  overflow-y: auto;
  background: linear-gradient(180deg, #0a0a1a 0%, #111128 100%);
  color: white;
`;

const Banner = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
`;

const BannerTitle = styled.div`
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.6);
`;

const BannerHint = styled.div`
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.45);
  line-height: 1.4;
  margin-top: 6px;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.04);
`;

const Label = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.92);
`;

const Segmented = styled.div`
  display: inline-flex;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
  overflow: hidden;
  align-self: flex-start;
`;

const SegBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 10px;
  font: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  background: ${p => p.$active ? p.$activeBg : 'transparent'};
  color: ${p => p.$active ? '#0a0a1a' : 'rgba(255,255,255,0.65)'};
  border: none;
  cursor: pointer;
  &:not(:last-child) {
    border-right: 1px solid rgba(255, 255, 255, 0.08);
  }
  &:hover { background: ${p => p.$active ? p.$activeBg : 'rgba(255,255,255,0.08)'}; }
  &:disabled { cursor: progress; opacity: 0.7; }
`;

const ErrorBox = styled.div`
  margin: 12px 16px;
  padding: 10px 14px;
  background: rgba(220, 38, 38, 0.15);
  border: 1px solid rgba(220, 38, 38, 0.35);
  color: #fca5a5;
  font-size: 0.78rem;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const SAVING_ICON_SIZE = 14;

export default function SectionVisibilityPanel() {
  const { instanceId } = useReportEdits();
  const [settings, setSettings] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch current settings from instance (don't trust stale parent state)
  const refetch = useCallback(async () => {
    if (!instanceId) { setLoading(false); return; }
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCE(instanceId));
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSettings(data?.settings || {});
      setError(null);
    } catch (e) {
      setError(`Asetusten lataaminen epäonnistui: ${e.message}`);
    } finally {
      setLoading(false);
    }
  }, [instanceId]);

  useEffect(() => { refetch(); }, [refetch]);

  if (!instanceId) {
    return (
      <Container>
        <Banner>
          <BannerTitle>Osioiden näkyvyys</BannerTitle>
          <BannerHint>Instanssi-id puuttuu.</BannerHint>
        </Banner>
      </Container>
    );
  }

  const setAccess = async (sec, next) => {
    const prevAccess = settings?.reportSectionAccess || {};
    const nextAccess = { ...prevAccess, [sec.id]: next };
    const nextSettings = { ...settings, reportSectionAccess: nextAccess };
    setSettings(nextSettings);
    setSavingId(sec.id);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.INSTANCE(instanceId), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ settings: { reportSectionAccess: nextAccess } }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      // The report holds its own copy of `instanceInfo.settings`. PATCH
      // doesn't invalidate it, so the section gates would still render
      // stale until the next full reload. Broadcast the change so the
      // report can merge it in-memory and the toggle feels instant.
      window.dispatchEvent(new CustomEvent('kuutio:section-access-changed', {
        detail: { instanceId, sectionId: sec.id, access: next, reportSectionAccess: nextAccess },
      }));
    } catch (e) {
      setSettings(settings); // rollback
      setError(`Tallennus epäonnistui: ${e.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const reportPin = settings?.hasReportPin;

  return (
    <Container>
      <Banner>
        <BannerTitle>Osioiden näkyvyys</BannerTitle>
        <BannerHint>
          Piilotettu = ei näy kenellekään. Julkinen = näkyy kaikille. Suojattu = vaatii PIN-koodin.
          {!reportPin && (
            <> {' '}Suojattu-tila ei toimi ennen kuin asetat <code>reportPin</code>-arvon
              instanssin asetuksiin.</>
          )}
        </BannerHint>
      </Banner>

      {error && (
        <ErrorBox>
          <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{error}</span>
        </ErrorBox>
      )}

      {loading
        ? <Row><Label><Loader2 size={SAVING_ICON_SIZE} style={{ animation: 'spin 1s linear infinite' }} /> Ladataan…</Label></Row>
        : SECTIONS.map(sec => {
            const access = resolveSectionAccess(settings, sec.id, sec.defaultVisible);
            const saving = savingId === sec.id;
            return (
              <Row key={sec.id}>
                <Label>
                  {access === ACCESS_HIDDEN ? <EyeOff size={14} /> :
                   access === ACCESS_AUTHED ? <Lock size={14} /> :
                   <Eye size={14} />}
                  {sec.label}
                </Label>
                {/* Order matches the visibility ladder: open → gated → hidden.
                    Easier to scan than the previous Hidden/Public/Authed
                    order — eye flows left to right as access narrows. */}
                <Segmented role="radiogroup" aria-label={`${sec.label} — näkyvyys`}>
                  <SegBtn
                    type="button"
                    role="radio"
                    aria-checked={access === ACCESS_PUBLIC}
                    $active={access === ACCESS_PUBLIC}
                    $activeBg="#86efac"
                    onClick={() => !saving && setAccess(sec, ACCESS_PUBLIC)}
                    disabled={saving}
                    title="Näytä kaikille"
                  >
                    <Eye size={11} /> Julkinen
                  </SegBtn>
                  <SegBtn
                    type="button"
                    role="radio"
                    aria-checked={access === ACCESS_AUTHED}
                    $active={access === ACCESS_AUTHED}
                    $activeBg="#fde68a"
                    onClick={() => !saving && setAccess(sec, ACCESS_AUTHED)}
                    disabled={saving || !reportPin}
                    title={reportPin ? 'Vaatii PIN-koodin' : 'Aseta ensin reportPin'}
                  >
                    <Lock size={11} /> Suojattu
                  </SegBtn>
                  <SegBtn
                    type="button"
                    role="radio"
                    aria-checked={access === ACCESS_HIDDEN}
                    $active={access === ACCESS_HIDDEN}
                    $activeBg="#9ca3af"
                    onClick={() => !saving && setAccess(sec, ACCESS_HIDDEN)}
                    disabled={saving}
                    title="Piilota osio kokonaan"
                  >
                    <EyeOff size={11} /> Piilotettu
                  </SegBtn>
                </Segmented>
                {saving && (
                  <Loader2 size={SAVING_ICON_SIZE} style={{ animation: 'spin 1s linear infinite', color: 'rgba(255,255,255,0.6)' }} />
                )}
              </Row>
            );
          })}
    </Container>
  );
}
